import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/Card";
import { Btn } from "@/components/Btn";
import { SectionHeader } from "@/components/SectionHeader";
import { SettingsRow, S_INPUT } from "@/components/SettingsRow";
import { Toggle } from "@/components/Toggle";
import { ApiError } from "@/api/client";
import { SettingsConfig, getSettingsConfig, putSettingsConfig } from "@/api/settings";

type Props = { isAdmin: boolean };

type FormulareCfg = Pick<
  SettingsConfig,
  | "FORMULARE_ANFRAGE_AKTIV"
  | "FORMULARE_KONUS_AKTIV"
  | "KONUS_HOTELID"
  | "KONUS_KATEGORIE_ERWACHSEN"
  | "KONUS_KATEGORIE_KIND"
  | "KONUS_KATEGORIE_KLEINKIND"
>;

const EMPTY: FormulareCfg = {
  FORMULARE_ANFRAGE_AKTIV: "1",
  FORMULARE_KONUS_AKTIV: "1",
  KONUS_HOTELID: "0011",
  KONUS_KATEGORIE_ERWACHSEN: "1",
  KONUS_KATEGORIE_KIND: "2",
  KONUS_KATEGORIE_KLEINKIND: "3",
};

function pickFormulareCfg(c: SettingsConfig): FormulareCfg {
  return {
    FORMULARE_ANFRAGE_AKTIV: c.FORMULARE_ANFRAGE_AKTIV,
    FORMULARE_KONUS_AKTIV: c.FORMULARE_KONUS_AKTIV,
    KONUS_HOTELID: c.KONUS_HOTELID,
    KONUS_KATEGORIE_ERWACHSEN: c.KONUS_KATEGORIE_ERWACHSEN,
    KONUS_KATEGORIE_KIND: c.KONUS_KATEGORIE_KIND,
    KONUS_KATEGORIE_KLEINKIND: c.KONUS_KATEGORIE_KLEINKIND,
  };
}

function CopyableUrl({ value, hint }: { value: string; hint?: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          readOnly
          value={value}
          style={{
            flex: 1,
            maxWidth: 360,
            padding: "7px 11px",
            border: "1px solid var(--border-strong)",
            borderRadius: 8,
            fontFamily: "var(--font-body)",
            fontSize: 13,
            color: "var(--fg-muted)",
            background: "var(--bg-subtle)",
            outline: "none",
            boxSizing: "border-box" as const,
          }}
        />
        <button
          type="button"
          onClick={handleCopy}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "7px 12px",
            border: "1px solid var(--border-strong)",
            borderRadius: 8,
            background: "var(--bg-card)",
            color: "var(--fg-muted)",
            fontSize: 13,
            fontFamily: "var(--font-body)",
            cursor: "pointer",
            whiteSpace: "nowrap" as const,
          }}
        >
          <span className="material-icons-outlined" style={{ fontSize: 16 }}>
            {copied ? "check" : "content_copy"}
          </span>
          {copied ? "Kopiert" : "Kopieren"}
        </button>
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 4 }}>{hint}</div>
      )}
    </div>
  );
}

export function FormulareSection({ isAdmin }: Props) {
  const qc = useQueryClient();
  const configQ = useQuery({ queryKey: ["settings-config"], queryFn: getSettingsConfig });

  const [cfg, setCfg] = useState<FormulareCfg>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const anfrageUrl = `${window.location.origin}/app/anfrage`;
  const konusBasisUrl = `${window.location.origin}/app/konus/`;

  useEffect(() => {
    if (configQ.data) {
      setCfg(pickFormulareCfg(configQ.data));
      setDirty(false);
    }
  }, [configQ.data]);

  const saveMut = useMutation({
    mutationFn: () => putSettingsConfig({ ...configQ.data!, ...cfg }),
    onSuccess: async (next) => {
      setCfg(pickFormulareCfg(next));
      setDirty(false);
      setMsg("Gespeichert.");
      await qc.invalidateQueries({ queryKey: ["settings-config"] });
    },
    onError: (e: unknown) => {
      setMsg(e instanceof ApiError ? e.message : "Speichern fehlgeschlagen.");
    },
  });

  function set<K extends keyof FormulareCfg>(key: K, val: string) {
    setCfg((prev) => ({ ...prev, [key]: val }));
    setDirty(true);
    setMsg(null);
  }

  return (
    <>
      {!isAdmin && (
        <Card style={{ marginBottom: 16, background: "var(--bg-subtle)" }}>
          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            <span className="material-icons-outlined" style={{ fontSize: 16, verticalAlign: "-3px" }}>
              info
            </span>{" "}
            Nur lesbar — Änderungen können nur Administratoren speichern.
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <SectionHeader
          title="Buchungsanfrage-Formular"
          desc="Das öffentliche Formular, über das Gruppen Anfragen stellen."
        />
        <SettingsRow label="Formular aktiv" hint="Wenn deaktiviert, ist das Formular für Besucher nicht mehr zugänglich.">
          <Toggle
            on={cfg.FORMULARE_ANFRAGE_AKTIV === "1"}
            onChange={(next) => set("FORMULARE_ANFRAGE_AKTIV", next ? "1" : "0")}
            disabled={!isAdmin}
          />
        </SettingsRow>
        <SettingsRow label="Öffentliche URL" hint="Zum Einbinden in die Website">
          <CopyableUrl value={anfrageUrl} />
        </SettingsRow>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          {msg && (
            <span style={{ fontSize: 12, color: "var(--fg-muted)", alignSelf: "center" }}>
              {msg}
            </span>
          )}
          <Btn
            variant="primary"
            icon="check"
            onClick={() => saveMut.mutate()}
            disabled={!isAdmin || !dirty || saveMut.isPending}
          >
            Speichern
          </Btn>
        </div>
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <SectionHeader
          title="Konus-Daten-Formular"
          desc="Das Formular, über das Gruppen ihre Konus-Daten eingeben."
        />
        <SettingsRow label="Formular aktiv" hint="Wenn deaktiviert, sind Konus-Links für Gruppen nicht mehr abrufbar.">
          <Toggle
            on={cfg.FORMULARE_KONUS_AKTIV === "1"}
            onChange={(next) => set("FORMULARE_KONUS_AKTIV", next ? "1" : "0")}
            disabled={!isAdmin}
          />
        </SettingsRow>
        <SettingsRow label="Basis-URL" hint="Das buchungsspezifische Token wird beim Versand des Konus-Links automatisch angehängt.">
          <CopyableUrl value={konusBasisUrl} />
        </SettingsRow>
      </Card>

      <Card>
        <SectionHeader
          title="AVS-Meldeschein-Export"
          desc="Werte für die XML-Importdatei der Gemeinde (AVS-Meldeschein). Diese erhältst du von der zuständigen Kurverwaltung."
        />
        <SettingsRow label="Objektnummer (hotelid)" hint="4-stellige Objektnummer, z. B. 0011">
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            disabled={!isAdmin}
            value={cfg.KONUS_HOTELID}
            onChange={(e) => set("KONUS_HOTELID", e.target.value)}
          />
        </SettingsRow>
        <SettingsRow label="Kategorie Erwachsener" hint="AVS-Kategorienummer (ab 16 J.)">
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            type="number"
            min={0}
            disabled={!isAdmin}
            value={cfg.KONUS_KATEGORIE_ERWACHSEN}
            onChange={(e) => set("KONUS_KATEGORIE_ERWACHSEN", e.target.value)}
          />
        </SettingsRow>
        <SettingsRow label="Kategorie Kind/Jugendlicher" hint="AVS-Kategorienummer (6–15 J.)">
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            type="number"
            min={0}
            disabled={!isAdmin}
            value={cfg.KONUS_KATEGORIE_KIND}
            onChange={(e) => set("KONUS_KATEGORIE_KIND", e.target.value)}
          />
        </SettingsRow>
        <SettingsRow label="Kategorie Kleinkind" hint="AVS-Kategorienummer (0–5 J.)">
          <input
            style={{ ...S_INPUT, maxWidth: 140 }}
            type="number"
            min={0}
            disabled={!isAdmin}
            value={cfg.KONUS_KATEGORIE_KLEINKIND}
            onChange={(e) => set("KONUS_KATEGORIE_KLEINKIND", e.target.value)}
          />
        </SettingsRow>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          {msg && (
            <span style={{ fontSize: 12, color: "var(--fg-muted)", alignSelf: "center" }}>
              {msg}
            </span>
          )}
          <Btn
            variant="primary"
            icon="check"
            onClick={() => saveMut.mutate()}
            disabled={!isAdmin || !dirty || saveMut.isPending}
          >
            Speichern
          </Btn>
        </div>
      </Card>
    </>
  );
}
